import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, CheckCircle2, XCircle, ArrowLeft, Key } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, valid, invalid, success, error
  const [email, setEmail] = useState('');

  const emailParam = searchParams.get('email');
  const token = searchParams.get('token');
  const type = searchParams.get('type') || 'marketing';

  useEffect(() => {
    const verifyToken = async () => {
      if (!emailParam || !token) {
        setStatus('invalid');
        return;
      }

      try {
        await axios.get(`${API_URL}/api/email/unsubscribe/verify`, {
          params: { email: emailParam, token }
        });
        setEmail(emailParam);
        setStatus('valid');
      } catch (error) {
        setStatus('invalid');
      }
    };

    verifyToken();
  }, [emailParam, token]);

  const handleUnsubscribe = async () => {
    try {
      await axios.post(`${API_URL}/api/email/unsubscribe`, {
        email: emailParam,
        token,
        unsubscribe_type: type
      });
      setStatus('success');
    } catch (error) {
      setStatus('error');
    }
  };

  const getTypeLabel = () => {
    switch (type) {
      case 'marketing': return 'emails promocionais e novidades';
      case 'security': return 'alertas de segurança';
      case 'transactional': return 'emails transacionais';
      case 'all': return 'todos os emails';
      default: return 'emails';
    }
  };

  return (
    <div className="min-h-screen bg-stone-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <Key className="w-8 h-8 text-amber-500" />
            <span className="text-2xl font-serif text-stone-800">Author's Key</span>
          </Link>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          {status === 'loading' && (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-stone-600">Verificando...</p>
            </div>
          )}

          {status === 'valid' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-amber-600" />
              </div>
              <h1 className="text-2xl font-serif text-stone-800 mb-4">
                Cancelar inscrição
              </h1>
              <p className="text-stone-600 mb-6">
                Você está prestes a cancelar sua inscrição de <strong>{getTypeLabel()}</strong> para o email:
              </p>
              <p className="text-stone-800 font-medium bg-stone-100 rounded-lg py-3 px-4 mb-6">
                {email}
              </p>
              <Button
                onClick={handleUnsubscribe}
                className="w-full bg-amber-500 hover:bg-amber-600 text-stone-900 mb-4"
              >
                Confirmar cancelamento
              </Button>
              <Link
                to="/dashboard"
                className="text-sm text-stone-500 hover:text-stone-700 inline-flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar para o app
              </Link>
            </div>
          )}

          {status === 'invalid' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-serif text-stone-800 mb-4">
                Link inválido
              </h1>
              <p className="text-stone-600 mb-6">
                Este link de cancelamento é inválido ou expirou. Se você deseja gerenciar suas preferências de email, acesse as configurações da sua conta.
              </p>
              <Link to="/configuracoes">
                <Button className="w-full bg-amber-500 hover:bg-amber-600 text-stone-900">
                  Ir para configurações
                </Button>
              </Link>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-serif text-stone-800 mb-4">
                Inscrição cancelada
              </h1>
              <p className="text-stone-600 mb-6">
                Você não receberá mais {getTypeLabel()}. 
                Se mudar de ideia, você pode reativar nas configurações da sua conta.
              </p>
              <Link to="/dashboard">
                <Button className="w-full bg-amber-500 hover:bg-amber-600 text-stone-900 mb-4">
                  Voltar para o app
                </Button>
              </Link>
              <p className="text-xs text-stone-400">
                Alterações podem levar até 24 horas para serem aplicadas.
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-serif text-stone-800 mb-4">
                Erro ao processar
              </h1>
              <p className="text-stone-600 mb-6">
                Ocorreu um erro ao processar sua solicitação. Por favor, tente novamente ou entre em contato conosco.
              </p>
              <Button
                onClick={() => setStatus('valid')}
                className="w-full bg-amber-500 hover:bg-amber-600 text-stone-900"
              >
                Tentar novamente
              </Button>
            </div>
          )}
        </div>

        <p className="text-center text-stone-500 text-sm mt-6">
          © 2025 Author's Key. Todos os direitos reservados.
        </p>
      </div>
    </div>
  );
}
